import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import React, { useCallback } from 'react';

import { cn } from '@/lib/tw-merge/cn';
import { type NotificationToastProps } from '@/types/notifications';

/**
 * Individual notification toast component
 */
const NotificationToast = React.forwardRef<
  HTMLDivElement,
  NotificationToastProps
>(
  (
    { notification, onClose, className, size = 'large', isCentralize = false },
    ref
  ) => {
    // Use the agent information that was captured when the notification was created
    // This ensures the agent info remains consistent even if activeAgent changes
    const agentToUse = notification.activeAgent || {
      agentName: 'Agent',
      avatar: '', // This should not happen since we now always capture agent info
    };

    // Handle close button click
    const handleClose = useCallback(() => {
      onClose(notification.id);
    }, [notification.id, onClose]);

    // Handle keyboard navigation for close button
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClose();
        }
      },
      [handleClose]
    );

    // Get styles based on notification type
    const getNotificationStyles = () => {
      const type = notification.type || 'info';

      switch (type) {
        case 'success':
          return {
            container: 'bg-[#edf7ed] border-[#3e8e58]',
            inner: 'bg-[#fbfffb]',
          };
        case 'error':
          return {
            container: 'bg-brand-100 border-primary',
            inner: 'bg-white',
          };
        case 'info':
        default:
          return {
            container: 'bg-brand-100 border-primary',
            inner: 'bg-white',
          };
      }
    };

    const styles = getNotificationStyles();

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{
          duration: 0.3,
          ease: [0.4, 0.0, 0.2, 1],
        }}
        className={cn(
          // Base container styles
          'flex items-center gap-3 rounded-lg border border-border-cool p-2 shadow-sm sm:p-3',
          styles.container,
          // Hover and focus states
          'transition-shadow duration-200 hover:shadow-md',
          // Responsive spacing
          'min-h-[50px] w-full',
          size === 'small' && 'p-2',
          className
        )}
        role="alert"
        aria-live="polite"
        aria-label={`Notification from ${agentToUse.agentName}: ${typeof notification.message === 'string' ? notification.message : 'Notification message'}`}
      >
        <div
          className={cn(
            'relative flex w-full rounded py-2',
            styles.inner,
            isCentralize && 'justify-center',
            size === 'large' && 'items-center px-2'
          )}
        >
          <div
            className={cn(
              'flex w-full min-w-0 gap-3 pr-8 md:pr-0',
              size === 'small'
                ? 'flex-col items-center'
                : 'flex-row items-start sm:items-center'
            )}
          >
            <div className="flex items-center gap-2">
              {/* Agent Avatar */}
              <div className="size-8 flex-shrink-0 overflow-hidden rounded-full bg-brand-surface sm:size-10">
                <img
                  src={agentToUse.avatar}
                  alt={`${agentToUse.agentName} avatar`}
                  className="size-full object-cover"
                  onError={e => {
                    // Fallback to a default background color if image fails
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>

              {/* Agent Name */}
              <span className="flex-shrink-0 text-sm font-medium text-foreground-strong">
                {agentToUse.agentName}
              </span>
            </div>

            {/* Message */}
            <span
              className={cn(
                notification.additionalImage ? 'w-[70%]' : 'min-w-0 flex-1',
                !isCentralize && 'whitespace-normal break-words',
                size === 'small' && 'text-center',
                'text-xs text-gray-700 sm:text-sm'
              )}
            >
              {notification.message}
            </span>

            {/* Optional Additional Image */}
            {notification.additionalImage && (
              <div className="flex-shrink-0">
                <img
                  src={notification.additionalImage}
                  alt="Additional notification image"
                  className={cn(
                    // Default styling for additional image
                    'size-8 object-contain',
                    // Apply custom classes if provided
                    notification.additionalImageClassname
                  )}
                  onError={e => {
                    // Hide the image if it fails to load
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
          {/* Close Button */}
          <button
            onClick={handleClose}
            onKeyDown={handleKeyDown}
            className={cn(
              // Base button styles
              'absolute right-2 top-2 flex size-5 items-center justify-center rounded-full md:relative md:right-0 md:top-0',
              // Interactive states
              'hover:bg-gray-100 focus:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
              // Transition
              'transition-colors duration-150'
            )}
            aria-label={`Close notification from ${agentToUse.agentName}`}
            title="Close notification"
            type="button"
          >
            <X
              className="size-4 text-foreground-strong hover:text-gray-700"
              strokeWidth={2}
            />
          </button>
        </div>
      </motion.div>
    );
  }
);

// Add display name for better debugging
NotificationToast.displayName = 'NotificationToast';

export default NotificationToast;
